import { FC, useEffect, useState } from 'react';
import { AppMenu as SharedAppMenu, AppMenuItem } from '@hssoft/design-system-shared-lib';
import { t } from 'i18next';
import { C_Hs_App } from '../../../../../../../graphql/generatedModel';
import { HsAppsListRes, topbarService } from '../../services/topBar.service';
import { responseHandler } from '../../../../../../responseHandler/responseHandler';
import { dataHelper } from '../../../../../../helpers/data/data.helper';
import {
  CashAssistIcon,
  WawiAssistIcon,
  RezeptAssistIcon,
  SmartPickingIcon,
  RestaurantManagerIcon,
  ConsoleIcon,
} from '../../../../../../../svg/custom.svg';

export const AppMenu: FC = () => {
  const [appsInfo, setAppsInfo] = useState<AppMenuItem[]>([]);
  const [isShow, setIsShow] = useState(true);

  useEffect(() => {
    const sub = topbarService.sub
      .hsAppsList()
      .pipe(responseHandler<HsAppsListRes>({ errorReturnType: [] }))
      .subscribe((appsAvailability) => {
        const availableAppTypes = (appsAvailability ?? [])
          .filter(({ isAvailable, appType }) => isAvailable && !deletedApps.has(appType))
          .map(({ appType }) => appType);

        const onlyWawiAvailable =
          availableAppTypes.length === 1 && availableAppTypes[0] === C_Hs_App.WAWI_ASSIST;

        if (!availableAppTypes.length || onlyWawiAvailable) {
          setAppsInfo([]);
          setIsShow(false);
          return;
        }

        const items: AppMenuItem[] = [
          ...availableAppTypes.map<AppMenuItem>((appType) => ({
            ...appsLabelUrl[appType]!,
            isCurrent: appType === C_Hs_App.WAWI_ASSIST,
          })),
          consoleApp,
        ];

        const sortedItems = dataHelper
          .data(items)
          .sort({ sortModel: [{ field: 'label1', sort: 'asc' }] })
          .result() as AppMenuItem[];

        setAppsInfo(sortedItems);
        setIsShow(true);
      });

    topbarService.pub.hsAppsList();
    return () => {
      sub?.unsubscribe();
    };
  }, []);

  if (!isShow) {
    return <></>;
  }

  return <SharedAppMenu appsInfo={appsInfo} />;
};

const iconSx = { width: 35, height: 30 } as const;

const appsLabelUrl: Partial<Record<C_Hs_App, AppMenuItem>> = {
  [C_Hs_App.CASH_ASSIST]: {
    label1: t('common.cash'),
    label2: t('common.assist'),
    url: 'https://cashassist.hssoft.com/',
    icon: <CashAssistIcon sx={iconSx} />,
  },
  [C_Hs_App.WAWI_ASSIST]: {
    label1: t('common.wawi'),
    label2: t('common.assist'),
    url: 'https://wawiassist.hssoft.com/',
    icon: <WawiAssistIcon sx={iconSx} />,
  },
  [C_Hs_App.REZEPT_ASSIST]: {
    label1: t('common.rezept'),
    label2: t('common.assist'),
    url: 'https://rezeptassist.web.hssoft.com/',
    icon: <RezeptAssistIcon sx={iconSx} />,
  },
  [C_Hs_App.SMART_PICKING]: {
    label1: t('common.smart'),
    label2: t('common.picking'),
    url: 'https://smartpicking.hssoft.com/',
    icon: <SmartPickingIcon sx={iconSx} />,
  },
  [C_Hs_App.RESTAURANT_MANAGER]: {
    label1: t('common.restaurant'),
    label2: t('common.manager'),
    url: 'https://restaurant-manager24.hssoft.com',
    icon: <RestaurantManagerIcon sx={iconSx} />,
  },
};

const deletedApps = new Set<C_Hs_App>([C_Hs_App.RUN_MY_RESTO]);

const consoleApp: AppMenuItem = {
  label1: t('common.console'),
  url: 'https://console.hssoft.com/',
  icon: <ConsoleIcon sx={iconSx} />,
};
